// ポータル向け API（external-design/portal-api.md）の呼び出し。ID トークンを Bearer で送る。

import { currentUser } from "./auth";
import { getConfig } from "./config";

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string) {
    super(code);
  }
}

async function call<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth) {
    const u = await currentUser();
    if (!u?.id_token) throw new ApiError(401, "not_signed_in");
    headers.Authorization = `Bearer ${u.id_token}`;
  }
  const res = await fetch(`${getConfig().portalApiBase}${path}`, {
    method, headers, body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (json as { code?: string }).code ?? "error");
  return json as T;
}

export interface Team { teamId: string; name: string }
export type Role = "admin" | "member";
export interface Service { subsystem: string; serviceUrl: string; enabled: boolean }
export interface MeMembership {
  contract: { contractId: string; name: string };
  status: "invited" | "active" | "disabled";
  teams: Team[];
  isAdmin: boolean;
  services: Service[];
}
export type PlatformRole = "system-admin" | null;
export interface Me { sub: string; email: string; platformRole: PlatformRole; memberships: MeMembership[] }
export interface UserRow {
  sub: string; email: string | null; role: Role; status: "invited" | "active" | "disabled";
  invitedAt: string | null; lastInvitedAt: string | null; inviteCount: number | null; expiresAt: string | null;
  acceptedAt: string | null; activatedAt: string | null; lastLoginAt: string | null;
  teams: Team[]; services: Record<string, boolean>;
}
/** system-admin（契約のユーザーとは兼ねない。ADR-0028）。 */
export interface SystemAdmin { sub: string; email: string; createdAt: string | null; lastLoginAt: string | null }
export interface Subscription { subsystem: string; subscriptionActive: boolean; serviceUrl: string }
export type ContractStatus = "active" | "suspended";
/** contractId は契約 ID（変更不可）、name は表示名、legalName は正式名称（system-admin だけが変更）。 */
export interface Contract { contractId: string; name: string; legalName?: string; status: ContractStatus }
export interface ContractRows { contract: Contract; subscriptions: Subscription[]; teams: Team[] }

// system-admin が扱う台帳（portal-api.md「システム管理者向け」）
export const SUBSYSTEMS = ["ignis", "orbis", "signa", "terra"] as const;
export type Subsystem = (typeof SUBSYSTEMS)[number];
export type AudienceOwner = Subsystem | "portal";
/** 認証ソースマッピング（閲覧用）。書き込みはデプロイ時のレジストリ同期だけ（ADR-0018）。 */
export interface Mapping { iss: string; poolKind: Issuer["poolKind"] | null; providerName: string; emailDomain: string; idpName: string | null }
export interface Issuer {
  iss: string; userPoolId: string; poolKind: "shared" | "dedicated"; hostedUiDomain?: string;
  audiences: Record<string, AudienceOwner>;
}

export const api = {
  me: () => call<Me>("GET", "/me"),
  contract: (contractId: string) => call<ContractRows>("GET", `/contracts/${contractId}`),
  users: (contractId: string, teamId?: string) =>
    call<{ users: UserRow[] }>("GET", `/contracts/${contractId}/users${teamId ? `?teamId=${encodeURIComponent(teamId)}` : ""}`),
  updateUser: (contractId: string, sub: string, patch: { role?: Role; teams?: string[]; status?: string; services?: Record<string, boolean> | null }) =>
    call<UserRow>("PATCH", `/contracts/${contractId}/users/${sub}`, patch),
  removeUser: (contractId: string, sub: string) => call<void>("DELETE", `/contracts/${contractId}/users/${sub}`),
  /** 本人性と所属（invited）を作るだけで、招待メールは送らない。 */
  addUser: (contractId: string, body: { email: string; role?: Role; teams?: string[]; services?: Record<string, boolean> }) =>
    call<UserRow>("POST", `/contracts/${contractId}/users`, body),
  /** invited の所属に招待メールを送る（初回も再送も同じ）。 */
  sendInvitation: (contractId: string, sub: string) => call<UserRow>("POST", `/contracts/${contractId}/invitations/${sub}`),
  cancelInvitation: (contractId: string, sub: string) => call<void>("DELETE", `/contracts/${contractId}/invitations/${sub}`),
  teams: (contractId: string) => call<{ teams: Team[] }>("GET", `/contracts/${contractId}/teams`),
  createTeam: (contractId: string, body: { name: string }) => call<Team>("POST", `/contracts/${contractId}/teams`, body),
  updateTeam: (contractId: string, teamId: string, body: { name: string }) =>
    call<Team>("PATCH", `/contracts/${contractId}/teams/${teamId}`, body),
  deleteTeam: (contractId: string, teamId: string) => call<void>("DELETE", `/contracts/${contractId}/teams/${teamId}`),
  adminContracts: () => call<{ contracts: Contract[] }>("GET", "/admin/contracts"),
  createContract: (body: { contractId: string; name: string; legalName: string }) => call<ContractRows>("POST", "/admin/contracts", body),
  /** admin は表示名と状態、system-admin はさらに正式名称を変えられる。 */
  updateContract: (contractId: string, patch: { name?: string; legalName?: string; status?: ContractStatus }) =>
    call<Contract>("PATCH", `/contracts/${contractId}`, patch),
  putSubscription: (contractId: string, subsystem: Subsystem, body: Omit<Subscription, "subsystem">) =>
    call<Subscription>("PUT", `/admin/contracts/${contractId}/subscriptions/${subsystem}`, body),
  mappings: (contractId: string) => call<{ mappings: Mapping[] }>("GET", `/contracts/${contractId}/mappings`),
  issuers: () => call<{ issuers: Issuer[] }>("GET", "/admin/issuers"),
  systemAdmins: () => call<{ systemAdmins: SystemAdmin[] }>("GET", "/admin/system-admins"),
  /** 本人性を作って system-admin にする。メールは送らない。 */
  addSystemAdmin: (email: string) => call<SystemAdmin>("POST", "/admin/system-admins", { email }),
  removeSystemAdmin: (sub: string) => call<void>("DELETE", `/admin/system-admins/${sub}`),
  // 招待の受諾（公開。招待トークン）
  invitation: (token: string) =>
    call<{ email: string; contractId: string; contractName: string; expiresAt: string; method: "sso" | "password"; sso?: { idpName: string } }>(
      "GET", `/invitations/${encodeURIComponent(token)}`, undefined, false),
  idpLookup: async (domain: string) => {
    const res = await fetch(`${getConfig().authApiBase}/idp-lookup?domain=${encodeURIComponent(domain)}&subsystem=portal`);
    if (!res.ok) throw new ApiError(res.status, "idp_lookup_failed");
    return (await res.json()) as { idpName: string | null; userPoolId: string | null; hostedUiDomain: string | null; clientId: string | null };
  },
};

/** エラーコードの表示文言（portal.md）。 */
export function messageFor(e: unknown): string {
  const code = e instanceof ApiError ? e.code : "";
  const table: Record<string, string> = {
    not_registered: "このアカウントは登録されていません。管理者に招待を依頼してください。",
    not_portal_client: "このトークンではポータルを利用できません。",
    forbidden: "この操作を行う権限がありません。",
    not_found: "対象が見つかりません。画面を読み直してください。",
    contract_inactive: "この契約は停止中か、有効なサービス利用がありません。",
    already_member: "この email は既にこの契約に所属しています。",
    last_admin: "契約に管理者が 1 人は必要です。最後の管理者は降格・無効化・除外できません。",
    cannot_remove_self: "自分自身をシステム管理者から削除することはできません。",
    has_memberships: "この email は契約に所属しています。契約のユーザーはシステム管理者にできません。別の email を使ってください。",
    already_system_admin: "この email は既にシステム管理者です。",
    system_admin: "この email はシステム管理者です。システム管理者は契約のユーザーにできません。",
    unknown_team: "指定したチームがありません。",
    invalid_role: "権限は「管理者」か「メンバー」を指定してください。",
    team_has_members: "メンバーが残っているチームは削除できません。先に所属を外してください。",
    service_not_subscribed: "サービス利用の無いサービスは使用可にできません。",
    invalid_email: "メールアドレスの形式が正しくありません。",
    invitation_not_found: "この招待は無効です。期限切れか、取り消された可能性があります。",
    not_signed_in: "ログインしてください。",
    invalid_contract_id: "契約 ID は英小文字・数字・_ で、先頭を英小文字か数字にして 48 文字以内にしてください。",
    invalid_contract_name: "表示名を入力してください。",
    invalid_legal_name: "正式名称を入力してください。",
    invalid_contract_patch: "契約 ID は変更できません。",
    contract_exists: "この契約 ID は既に使われています。",
    invalid_status: "状態の指定が正しくありません。",
    invalid_subscription: "サービス利用の URL（https://）を入力してください。",
  };
  return table[code] ?? `エラーが発生しました（${code || String(e)}）`;
}
